import { useParams } from "react-router-dom";
import { Loader2, FileClock } from "lucide-react";
import SiteHeaderV2 from "@/components/v2/SiteHeaderV2";
import ComingSoon from "@/components/v2/ComingSoon";
import PaperLogTrainer from "@/components/paperlog/PaperLogTrainer";
import { getProTool } from "@/data/v2Config";
import { useAuth } from "@/context/AuthContext";

// Real, built Pro tools keyed by tool.key. Everything else falls back to ComingSoon.
const AVAILABLE = {
  "paper-logs": {
    eyebrow: "RouteHaul Pro",
    icon: FileClock,
    title: "Paper Log Practice",
    blurb: "Practice plotting a compliant 24-hour log by hand for when your ELD goes down — the app checks it against the Hours-of-Service rules.",
    Component: PaperLogTrainer,
  },
};

export default function ProToolPage() {
  const { key } = useParams();
  const tool = getProTool(key);
  const { user } = useAuth();
  const built = AVAILABLE[key];

  const body = () => {
    if (!tool) {
      return <ComingSoon title="Tool not found" description="We couldn't find that Pro tool." backTo="/v2/pro" backLabel="Back to Pro tools" />;
    }
    if (!built || tool.status !== "available") {
      return (
        <ComingSoon
          icon={tool.icon}
          eyebrow="RouteHaul Pro · Coming soon"
          title={tool.label}
          description={tool.desc}
          note="This Pro tool is on the roadmap. First we'll teach the concept in a mission, then this tool will help you put it to work — no guesswork."
          backTo="/v2/pro"
          backLabel="Back to Pro tools"
          testid="pro-tool-coming-soon"
        />
      );
    }
    // Paid gate
    if (user === null) {
      return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;
    }
    if (!user) {
      return (
        <ComingSoon
          icon={built.icon}
          eyebrow="Pro feature · Log in"
          title={built.title}
          description="Log in to your RouteHaul account to use Pro tools."
          backTo="/login"
          backLabel="Log in"
          testid="pro-tool-login-gate"
        />
      );
    }
    if (!user.subscription_active) {
      return (
        <ComingSoon
          icon={built.icon}
          eyebrow="Pro feature · Upgrade"
          title={built.title}
          description={built.blurb}
          note="Paper Log Practice is part of RouteHaul Pro. Upgrade to unlock the practice grid, instant HOS feedback and the knowledge quiz."
          backTo="/subscribe"
          backLabel="See Pro plans"
          testid="pro-tool-upgrade-gate"
        />
      );
    }
    const Tool = built.Component;
    return (
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">
          <FileClock className="w-4 h-4" /> {built.eyebrow}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">{built.title}</h1>
        <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">{built.blurb}</p>
        <div className="mt-8"><Tool /></div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeaderV2 />
      {body()}
    </div>
  );
}
